export const WFR_MODEL_VERSION = '2026-09-16.1';
export type Delimiter = 'auto' | ',' | ';' | '\t';
export type ColumnKey = 'name' | 'supplier' | 'author' | 'modified';
export type ColumnMap = Record<ColumnKey, number>;
export const DEFAULT_COLUMNS: ColumnMap = { name: 0, supplier: 1, author: 2, modified: 3 };
export const COLUMN_LABELS: Record<ColumnKey, string> = {
  name: 'Template', supplier: 'Supplier', author: 'Author', modified: 'Last modified',
};
const HEADERS: Record<ColumnKey, RegExp> = {
  name: /^(template|name|template name|class|document class)$/i,
  supplier: /^(supplier|vendor|supplier name|vendor name)$/i,
  author: /^(author|owner|created by|maintainer)$/i,
  modified: /^(modified|last modified|updated|last updated|date|last change)$/i,
};
export const normalizeName = (s: string) => s.normalize('NFC').trim().toLowerCase().replace(/\s+/gu, ' ');

interface Row { cells: string[]; line: number }
/** Strict RFC-style quoting, also usable for tab/semicolon-delimited exports. */
export function parseDelimited(text: string, delimiter: string): { rows: Row[]; errors: string[] } {
  const rows: Row[] = [];
  const errors: string[] = [];
  let cells: string[] = [], field = '', quoted = false, closed = false;
  let line = 1, rowLine = 1;
  const cell = () => { cells.push(field.trim()); field = ''; closed = false; };
  const row = () => {
    cell();
    if (cells.some(c => c !== '')) rows.push({ cells, line: rowLine });
    cells = []; rowLine = line + 1;
  };
  const input = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (quoted) {
      if (char === '"') {
        if (input[i + 1] === '"') { field += '"'; i++; }
        else { quoted = false; closed = true; }
      } else if (char === '\r' || char === '\n') {
        if (char === '\r' && input[i + 1] === '\n') i++;
        field += '\n'; line++;
      } else field += char;
    } else if (char === delimiter) cell();
    else if (char === '\r' || char === '\n') {
      row();
      if (char === '\r' && input[i + 1] === '\n') i++;
      line++;
    } else if (char === '"') {
      if (field.trim() || closed) errors.push(`Line ${line}: unexpected quote in an unquoted field.`);
      else { field = ''; quoted = true; }
    } else if (closed) {
      if (!/\s/.test(char)) errors.push(`Line ${line}: unexpected text after a closing quote.`);
    } else field += char;
  }
  if (quoted) errors.push(`Line ${rowLine}: quoted field is not closed.`);
  if (field || cells.length || closed) row();
  return { rows, errors: [...new Set(errors)] };
}

export function isoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
}

export interface Template {
  name: string; supplier: string; author: string; modifiedRaw: string;
  modified: Date | null; dateStatus: 'valid' | 'missing' | 'invalid' | 'future'; line: number;
}
export interface TemplateParseOptions {
  delimiter?: Delimiter;
  header?: 'auto' | 'present' | 'absent';
  columns?: ColumnMap;
  referenceDate: string;
}
export function parseTemplates(text: string, options: TemplateParseOptions) {
  const errors: string[] = [], warnings: string[] = [];
  let rows: Row[] = [], delimiter = options.delimiter ?? 'auto';
  let columns = options.columns ?? { ...DEFAULT_COLUMNS };
  const empty = () => ({ templates: [] as Template[], rows, errors, warnings, delimiter, columns, headers: [] as string[] });
  if (!text.trim()) return empty();
  if (!isoDate(options.referenceDate)) { errors.push('Choose a valid reference date.'); return empty(); }
  if (delimiter === 'auto') {
    const candidates = [',', '\t', ';'].map(value => ({ value, ...parseDelimited(text, value) }));
    const plausible = candidates.filter(c => !c.errors.length && c.rows.length && c.rows[0].cells.length >= 4);
    if (plausible.length !== 1) {
      errors.push(plausible.length ? 'More than one delimiter is plausible. Choose the delimiter explicitly.' : 'Cannot safely detect the delimiter. Choose comma, tab or semicolon, then check quotes and columns.');
      return empty();
    }
    delimiter = plausible[0].value as Delimiter;
  }
  const parsed = parseDelimited(text, delimiter);
  rows = parsed.rows;
  errors.push(...parsed.errors);
  if (errors.length || !rows.length) return empty();
  const first = rows[0].cells;
  const header = options.header ?? 'auto';
  if (header === 'auto') {
    const found = Object.fromEntries(Object.entries(HEADERS).map(([key, re]) => [key, first.flatMap((cell, i) => re.test(cell) ? [i] : [])])) as Record<ColumnKey, number[]>;
    if (Object.values(found).some(indices => indices.length !== 1)) {
      errors.push('Header is missing, incomplete or ambiguous. Choose “First row is a header” or “No header”, then confirm all four column mappings.');
      return empty();
    }
    columns = Object.fromEntries(Object.entries(found).map(([key, indices]) => [key, indices[0]])) as ColumnMap;
  }
  const headers = header === 'absent' ? first.map((_, i) => `Column ${i + 1}`) : first;
  if (new Set(Object.values(columns)).size !== 4 || Object.values(columns).some(i => !Number.isInteger(i) || i < 0 || i >= first.length)) {
    errors.push('Map each field to a different existing column. Four columns are required; empty author/date cells are allowed.');
    return { ...empty(), headers };
  }
  const dataRows = header === 'absent' ? rows : rows.slice(1);
  const templates: Template[] = [];
  const names = new Map<string, number>();
  for (const row of dataRows) {
    if (row.cells.length !== first.length) {
      errors.push(`Line ${row.line}: expected ${first.length} columns, found ${row.cells.length}. Check delimiters and quotes.`); continue;
    }
    const name = row.cells[columns.name];
    if (!name) { errors.push(`Line ${row.line}: template name is empty.`); continue; }
    const normalized = normalizeName(name);
    if (names.has(normalized)) warnings.push(`Line ${row.line}: duplicate template name (first seen on line ${names.get(normalized)}). Both rows are counted; review before interpreting totals.`);
    else names.set(normalized, row.line);
    const modifiedRaw = row.cells[columns.modified];
    const modified = isoDate(modifiedRaw);
    const dateStatus = !modifiedRaw ? 'missing' : !modified ? 'invalid' : modifiedRaw > options.referenceDate ? 'future' : 'valid';
    if (dateStatus === 'invalid') warnings.push(`Line ${row.line}: invalid or ambiguous date. Use YYYY-MM-DD; this row is excluded from date-age checks.`);
    if (dateStatus === 'future') warnings.push(`Line ${row.line}: modification date is after the reference date; check the export.`);
    templates.push({ name, supplier: row.cells[columns.supplier], author: row.cells[columns.author], modifiedRaw, modified, dateStatus, line: row.line });
  }
  if (!dataRows.length) warnings.push('The header is present, but there are no template rows.');
  // Do not present a plausible partial analysis when any row was malformed.
  return { templates: errors.length ? [] : templates, rows, errors, warnings, delimiter, columns, headers };
}

export function analyzeTemplates(templates: Template[], active: string[], staleYears: number, referenceDate: string) {
  const now = isoDate(referenceDate);
  if (!templates.length || !now || !Number.isFinite(staleYears) || staleYears < 1) return null;
  const cutoff = new Date(now);
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - staleYears);
  // February 29 maps to February 28 in a non-leap threshold year.
  if (cutoff.getUTCMonth() !== now.getUTCMonth()) cutoff.setUTCDate(0);
  const suppliers = new Map<string, { label: string; count: number }>();
  const authors = new Map<string, { label: string; count: number }>();
  const stale: Template[] = [];
  let noSupplier = 0, noDate = 0, invalidDates = 0, futureDates = 0, noAuthor = 0;
  for (const t of templates) {
    if (t.supplier) {
      const key = normalizeName(t.supplier), entry = suppliers.get(key) ?? { label: t.supplier, count: 0 };
      entry.count++; suppliers.set(key, entry);
    } else noSupplier++;
    if (t.author) {
      const key = normalizeName(t.author), entry = authors.get(key) ?? { label: t.author, count: 0 };
      entry.count++; authors.set(key, entry);
    } else noAuthor++;
    if (t.dateStatus === 'missing') noDate++;
    if (t.dateStatus === 'invalid') invalidDates++;
    if (t.dateStatus === 'future') futureDates++;
    if (t.dateStatus === 'valid' && t.modified && t.modified < cutoff) stale.push(t);
  }
  const authorRows = [...authors.values()].sort((a, b) => b.count - a.count);
  const topShare = authorRows.length ? Math.round(authorRows[0].count / templates.length * 100) : null;
  const activeSuppliers = [...new Map(active.filter(s => s.trim()).map(s => [normalizeName(s), s.trim()])).values()];
  const activeKeys = new Set(activeSuppliers.map(normalizeName));
  return {
    suppliers, noSupplier, noDate, invalidDates, futureDates, noAuthor, authorRows, topShare,
    stale: stale.sort((a, b) => a.modified!.getTime() - b.modified!.getTime()),
    multi: [...suppliers.values()].filter(s => s.count > 1),
    missing: activeSuppliers.filter(s => !suppliers.has(normalizeName(s))),
    orphaned: activeSuppliers.length ? [...suppliers.entries()].filter(([key]) => !activeKeys.has(key)).map(([, entry]) => entry) : [],
  };
}
